import type { InfiniteData } from '@tanstack/react-query';

/**
 * Shared infinite-query plumbing. The differing wire envelopes
 * ({items}/{trades}/{records}/{data}, cursor vs lek) are real and stay in each
 * hook module — only the continuation and flatten idioms are generic.
 */

/** Continue while the page carries a cursor; only a missing cursor ends pagination. */
export const nextCursorParam = (page: { cursor?: string }): string | undefined => page.cursor ?? undefined;

/** LEK twin of nextCursorParam — Dynamo-style last-evaluated-key pagination. */
export const nextLekParam = <L>(page: { lek?: L }): L | undefined => page.lek ?? undefined;

/**
 * Select factory flattening accumulated pages into one row array.
 *
 * Instantiate at MODULE level — a stable select reference matters: an inline
 * closure recreated per render defeats TanStack Query's select memoization and
 * re-flattens the full accumulated dataset on every render.
 *
 * `?? []` guards malformed/empty payloads — an undefined page items array would
 * smuggle `undefined` rows into the table and crash the render into the root
 * error boundary (review finding).
 */
export const flattenPages =
  <P, T>(getItems: (page: P) => T[] | undefined) =>
  (data: InfiniteData<P>): T[] =>
    data.pages.flatMap((page) => getItems(page) ?? []);
